import { useState } from "react";
import { Box, Switch, TextField, Typography, Button, Chip, CircularProgress, Divider, Link } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const glassCard = { p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.7)", backdropFilter: "blur(8px)", border: "1px solid rgba(229,231,235,0.6)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" };
const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff", fontSize: 12 } };
const accentText = (a) => ({ ...fieldSx, "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: a }, "& .MuiInputLabel-root.Mui-focused": { color: a } });

const RealTimeSearchTab = ({ skipOption, setSkipOption, errorMessage, setErrorMessage, settings, setSettings, accentColor = "#6366f1" }) => {
  const s = settings || {};
  const [testStatus, setTestStatus] = useState("idle");
  const [testResults, setTestResults] = useState([]);
  const [testError, setTestError] = useState("");
  const [testQuery, setTestQuery] = useState("");
  const [testRaw, setTestRaw] = useState(null);

  const extractList = (data) => {
    let list = Array.isArray(data) ? data : null;
    if (!list) {
      const path = (s.responsePath || "").trim();
      if (path && path !== "auto") {
        list = data;
        path.split(".").forEach((p) => { if (list && typeof list === "object") list = list[p]; });
      } else {
        list = data?.options || data?.data || data?.results || data?.items || [];
      }
    }
    return Array.isArray(list) ? list : [];
  };

  const mapItem = (o) => {
    if (typeof o === "object" && o !== null) {
      return o[s.labelField || "label"] || o[s.valueField || "value"] || o.name || o.title || String(o);
    }
    return String(o);
  };

  const handleTest = async () => {
    if (!s.apiEndpoint) {
      setTestStatus("error");
      setTestError("API endpoint is empty");
      return;
    }
    if (!testQuery.trim()) {
      setTestStatus("error");
      setTestError("Enter a test query");
      return;
    }
    setTestStatus("loading");
    setTestError("");
    setTestResults([]);
    setTestRaw(null);
    try {
      const paramName = s.queryParam || "q";
      const sep = s.apiEndpoint.includes("?") ? "&" : "?";
      const url = `${s.apiEndpoint}${sep}${paramName}=${encodeURIComponent(testQuery)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTestRaw(data);
      const list = extractList(data);
      const mapped = list.map(mapItem).filter(Boolean);
      if (mapped.length === 0) {
        setTestStatus("error");
        setTestError("API returned no results (check Response Path or Label/Value fields)");
        return;
      }
      setTestResults(mapped);
      setTestStatus("success");
    } catch (err) {
      setTestStatus("error");
      setTestError(err.message || "Request failed");
    }
  };

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1f2937" }}>Skip Option</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Allow visitors to skip this question</Typography>
          </Box>
          <Switch checked={!!skipOption} onChange={(e) => setSkipOption(e.target.checked)} size="small" />
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <SearchIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
          <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>API Source</Typography>
        </Box>
        <Typography sx={{ fontSize: 11, color: "#6b7280", mb: 1.5 }}>
          Search results fetched live as the visitor types. Configure the query param + response shape below to match your API.
        </Typography>

        <TextField
          fullWidth size="small" label="API Endpoint"
          placeholder="https://api.example.com/search"
          value={s.apiEndpoint || ""}
          onChange={(e) => {
            setSettings({ ...s, apiEndpoint: e.target.value });
            if (testStatus === "error" && e.target.value) {
              setTestStatus("idle");
              setTestError("");
            }
          }}
          sx={{ ...accentText(accentColor), mb: 1.5 }}
        />

        <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
          <TextField
            fullWidth size="small" label="Query Param Name"
            placeholder="q (default)"
            value={s.queryParam || ""}
            onChange={(e) => setSettings({ ...s, queryParam: e.target.value })}
            sx={fieldSx}
          />
          <TextField
            fullWidth size="small" label="Response Path"
            placeholder='auto (or e.g. "data.items")'
            value={s.responsePath || ""}
            onChange={(e) => setSettings({ ...s, responsePath: e.target.value })}
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth size="small" label="Label Field"
            placeholder="label (or name/title)"
            value={s.labelField || ""}
            onChange={(e) => setSettings({ ...s, labelField: e.target.value })}
            sx={fieldSx}
          />
          <TextField
            fullWidth size="small" label="Value Field"
            placeholder="value (or id)"
            value={s.valueField || ""}
            onChange={(e) => setSettings({ ...s, valueField: e.target.value })}
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <TextField
            fullWidth size="small" type="number" label="Min Characters"
            value={s.minChars ?? 2}
            onChange={(e) => setSettings({ ...s, minChars: Number(e.target.value) || 2 })}
            sx={fieldSx}
          />
          <TextField
            fullWidth size="small" type="number" label="Max Results"
            value={s.maxResults ?? 10}
            onChange={(e) => setSettings({ ...s, maxResults: Number(e.target.value) || 10 })}
            sx={fieldSx}
          />
          <TextField
            fullWidth size="small" type="number" label="Debounce (ms)"
            value={s.debounceMs ?? 300}
            onChange={(e) => setSettings({ ...s, debounceMs: Number(e.target.value) || 300 })}
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ mt: 1.5, p: 1.2, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
          <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: 600, mb: 0.5 }}>Supported response shapes:</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• ["A", "B", "C"]</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• {"{ options: [...] }"}</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• {"{ data: [{label, name, value}] }"}</Typography>
          <Typography sx={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>• {"{ results: [...] }"}</Typography>
        </Box>
      </Box>

      <Box sx={glassCard}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#1f2937" }}>Test API</Typography>
            <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Verify the endpoint returns expected results</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          <TextField
            fullWidth size="small" placeholder='Type a test query (e.g. "iphone")'
            value={testQuery}
            onChange={(e) => {
              setTestQuery(e.target.value);
              if (testStatus === "error" && e.target.value) {
                setTestStatus("idle");
                setTestError("");
              }
            }}
            onKeyDown={(e) => e.key === "Enter" && handleTest()}
            sx={fieldSx}
          />
          <Button
            size="small" variant="outlined"
            startIcon={testStatus === "loading" ? <CircularProgress size={12} /> : <PlayArrowIcon />}
            onClick={handleTest}
            disabled={testStatus === "loading"}
            sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, borderRadius: 2, minWidth: 110 }}
          >
            {testStatus === "loading" ? "Testing..." : "Run Test"}
          </Button>
        </Box>

        {testStatus === "error" && (
          <Box sx={{ p: 1.2, bgcolor: "#fef2f2", borderRadius: 1.5, border: "1px solid #fecaca" }}>
            <Typography sx={{ fontSize: 11, color: "#b91c1c", fontWeight: 600 }}>Error: {testError}</Typography>
            {testRaw !== null && (
              <Box sx={{ mt: 1, p: 1, bgcolor: "#fff", borderRadius: 1, border: "1px solid #fee2e2", maxHeight: 120, overflow: "auto" }}>
                <Typography sx={{ fontSize: 9, color: "#6b7280", fontWeight: 600, mb: 0.3 }}>Raw response (first 800 chars):</Typography>
                <Typography component="pre" sx={{ fontSize: 9, color: "#374151", fontFamily: "monospace", whiteSpace: "pre-wrap", m: 0 }}>
                  {JSON.stringify(testRaw, null, 2).slice(0, 800)}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {testStatus === "success" && (
          <Box>
            <Typography sx={{ fontSize: 11, color: "#15803d", fontWeight: 600, mb: 0.8 }}>
              Success — {testResults.length} result{testResults.length === 1 ? "" : "s"} for "{testQuery}"
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, p: 1.2, bgcolor: "#fff", borderRadius: 1.5, border: "1px solid #e5e7eb" }}>
              {testResults.slice(0, 8).map((r, i) => (
                <Box key={i} sx={{ px: 1.5, py: 0.7, fontSize: 12, color: "#374151", borderRadius: 1, "&:hover": { bgcolor: "#f9fafb" }, cursor: "pointer" }}>
                  {r}
                </Box>
              ))}
              {testResults.length > 8 && (
                <Typography sx={{ fontSize: 10, color: "#6b7280", textAlign: "center", pt: 0.5 }}>
                  +{testResults.length - 8} more
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Display</Typography>

        <TextField
          fullWidth size="small"
          label="Placeholder"
          placeholder="Type to search..."
          value={s.placeholder || ""}
          onChange={(e) => setSettings({ ...s, placeholder: e.target.value })}
          sx={{ ...accentText(accentColor), mb: 1.5 }}
        />

        <TextField
          fullWidth size="small"
          label="No results message"
          placeholder="No results found"
          value={s.noResultsMessage || ""}
          onChange={(e) => setSettings({ ...s, noResultsMessage: e.target.value })}
          sx={fieldSx}
        />
      </Box>

      <Box sx={glassCard}>
        <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 1, color: "#1f2937" }}>Error Message</Typography>
        <TextField fullWidth placeholder="Please enter a valid search" value={errorMessage || ""} size="small"
          onChange={(e) => setErrorMessage(e.target.value)}
          sx={fieldSx} />
      </Box>
    </Box>
  );
};

export default RealTimeSearchTab;
